import { none, Option, some } from '@sapphire/result';
import { Nullable } from '@ps/utils';
import { buyerAccessLevelOptions } from './buyer-access-level.helper';
import { findBuyerAccessLevelOption } from '../models/access-level-option.model';
import { ManagedUserDraft } from '../models/managed-user-draft.model';
import { MultiBuyerEntity } from '../models/multi-buyer-entity.model';
import { ProjectManager } from '../models/project-manager.model';
import { UserFormModel } from '../models/user-form.model';
import { UserViewModel } from '../state/user-state.model';

const primaryEntityOf = (
  form: UserFormModel,
  sources: UserViewModel,
): Nullable<MultiBuyerEntity> =>
  sources.multiBuyerEntities.find(
    (entity) => entity.id === form.primaryEntityId,
  ) ?? null;

const projectManagerOf = (
  form: UserFormModel,
  sources: UserViewModel,
): Nullable<ProjectManager> =>
  sources.projectManagers.find(
    (manager) => manager.id === form.defaultProjectManagerId,
  ) ?? null;

const buyerAccessLevelOf = (
  form: UserFormModel,
  sources: UserViewModel,
  primaryEntity: Nullable<MultiBuyerEntity>,
): ManagedUserDraft['buyerAccessLevel'] => {
  const company = sources.companyDetail;
  const key = form.buyerAccessLevelKey;

  if (!company || !key) {
    return null;
  }

  return findBuyerAccessLevelOption(
    buyerAccessLevelOptions(company, primaryEntity),
    key,
  );
};

/**
 * `none` when the form carries no selectable company — the effect turns that
 * into a failure action rather than calling the API with an incomplete draft.
 */
export const toManagedUserDraft = (
  form: UserFormModel,
  sources: UserViewModel,
): Option<ManagedUserDraft> => {
  const company = sources.companies.find(
    (candidate) => candidate.id === form.companyId,
  );

  if (!company) {
    return none;
  }

  const primaryEntity = primaryEntityOf(form, sources);

  return some({
    id: sources.selectedUser?.id ?? null,
    email: form.email,
    name: form.name,
    company,
    slackMemberId: form.slackMemberId,
    isApiUser: form.isApiUser,
    sendWelcomeEmail: form.sendWelcomeEmail,
    password: form.password,
    token: sources.apiToken,
    status: sources.selectedUser?.status ?? null,
    buyerAccessLevel: buyerAccessLevelOf(form, sources, primaryEntity),
    supplierAccessLevel: form.supplierAccessLevel
      ? { value: form.supplierAccessLevel, label: '' }
      : null,
    operatorAccessLevel: form.operatorAccessLevel
      ? { value: form.operatorAccessLevel, label: '' }
      : null,
    hasQuestionLibraryAccess: form.hasQuestionLibraryAccess,
    primaryEntity,
    defaultProjectManager: projectManagerOf(form, sources),
    wasMultiBuyerManager: !!sources.selectedUser?.isMultiBuyerManager,
  });
};
